'use client';

import React from 'react';
import { RotateCcw } from 'lucide-react';
import type { FilterState } from './TourPackagesClient';

const TRAVEL_TYPES = ['Family', 'Couple', 'Honeymoon', 'Adventure', 'Group'];
const SEASONS = ['Spring', 'Summer', 'Autumn', 'Winter'];
const BUDGET_MARKS = [10000, 20000, 30000, 40000, 50000];

interface PackageFiltersProps {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  onReset: () => void;
}

function toggleArrayItem(arr: string[], item: string): string[] {
  return arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item];
}

export default function PackageFilters({ filters, onChange, onReset }: PackageFiltersProps) {
  return (
    <div className="bg-white rounded-2xl border border-border shadow-sm p-5 flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-foreground text-base">Filters</h3>
        <button
          onClick={onReset}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors font-medium"
        >
          <RotateCcw size={13} />
          Reset
        </button>
      </div>

      {/* Duration */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">
          Duration
          <span className="text-muted-foreground font-normal ml-2 text-xs">
            {filters.durationMin}–{filters.durationMax} days
          </span>
        </h4>
        <div className="flex flex-col gap-2">
          {[
            { label: 'Any duration', min: 0, max: 15 },
            { label: '1–4 Days', min: 1, max: 4 },
            { label: '5–7 Days', min: 5, max: 7 },
            { label: '8–10 Days', min: 8, max: 10 },
            { label: '11+ Days', min: 11, max: 15 },
          ].map((opt) => (
            <label
              key={`dur-${opt.label}`}
              className="flex items-center gap-2.5 cursor-pointer group"
            >
              <input
                type="radio"
                name="duration"
                className="accent-primary w-4 h-4 cursor-pointer"
                checked={filters.durationMin === opt.min && filters.durationMax === opt.max}
                onChange={() =>
                  onChange({ ...filters, durationMin: opt.min, durationMax: opt.max })
                }
              />
              <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                {opt.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Budget */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">
          Max Budget
          <span className="text-primary font-bold ml-2 text-sm font-tabular">
            ₹{filters.budgetMax.toLocaleString('en-IN')}
          </span>
        </h4>
        <input
          type="range"
          min={5000}
          max={50000}
          step={1000}
          value={filters.budgetMax}
          onChange={(e) => onChange({ ...filters, budgetMax: Number(e.target.value) })}
          className="w-full accent-primary cursor-pointer"
        />
        <div className="flex items-center justify-between text-xs text-muted-foreground mt-1.5">
          <span>₹5,000</span>
          <span>₹50,000+</span>
        </div>
        <div className="flex gap-1.5 mt-2 flex-wrap">
          {BUDGET_MARKS.map((b) => (
            <button
              key={`budget-mark-${b}`}
              onClick={() => onChange({ ...filters, budgetMax: b })}
              className={`text-xs px-2 py-1 rounded-full border transition-all duration-150 ${
                filters.budgetMax === b
                  ? 'border-primary bg-primary-light text-primary font-semibold' :'border-border text-muted-foreground hover:border-primary hover:text-primary'
              }`}
            >
              ₹{(b / 1000).toFixed(0)}k
            </button>
          ))}
        </div>
      </div>

      {/* Travel Type */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">Travel Type</h4>
        <div className="flex flex-col gap-2">
          {TRAVEL_TYPES.map((type) => (
            <label key={`type-${type}`} className="flex items-center gap-2.5 cursor-pointer group">
              <input
                type="checkbox"
                className="accent-primary w-4 h-4 cursor-pointer rounded"
                checked={filters.travelTypes.includes(type)}
                onChange={() =>
                  onChange({ ...filters, travelTypes: toggleArrayItem(filters.travelTypes, type) })
                }
              />
              <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                {type}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Season */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">Best Season</h4>
        <div className="flex flex-wrap gap-2">
          {SEASONS.map((season) => (
            <button
              key={`season-${season}`}
              onClick={() =>
                onChange({ ...filters, seasons: toggleArrayItem(filters.seasons, season) })
              }
              className={`filter-chip text-xs ${filters.seasons.includes(season) ? 'active' : ''}`}
            >
              {season}
            </button>
          ))}
        </div>
      </div>

      {/* Budget Tier */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">Category</h4>
        <div className="flex flex-col gap-2">
          {[
            { label: 'Budget (Under ₹15k)', value: 15000 },
            { label: 'Mid-Range (₹15k–₹25k)', value: 25000 },
            { label: 'Premium (₹25k–₹35k)', value: 35000 },
            { label: 'Luxury (₹35k+)', value: 50000 },
          ].map((tier) => (
            <button
              key={`tier-${tier.label}`}
              onClick={() => onChange({ ...filters, budgetMax: tier.value })}
              className={`text-left text-sm px-3 py-2 rounded-lg border transition-all duration-150 ${
                filters.budgetMax === tier.value
                  ? 'border-primary bg-primary-light text-primary font-semibold' :'border-border text-muted-foreground hover:border-primary hover:text-primary'
              }`}
            >
              {tier.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}